import React, { useState } from 'react';
import { useLocation } from 'wouter';
import { useJobTitleFromUrl } from '@/components/DynamicCareerRoutes';
import { Button, Card, CardContent, CardHeader, CardTitle, Checkbox, Input, Label, Textarea } from "@/ui";
import { ArrowLeft, Upload, FileText, User, Briefcase } from 'lucide-react';

interface FormData {
  // Personal Information
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  address: string;
  city: string;
  state: string;
  zipCode: string;
  country: string;
  linkedin: string;
  twitter: string;
  github: string;
  website: string;
  
  // Professional Information
  currentPosition: string;
  currentCompany: string;
  yearsExperience: string;
  education: string;
  coverLetter: string;
  
  // Resume Upload
  resume: File | null;
  
  // Compliance Information
  race: string;
  veteranStatus: string;
  disabilityStatus: string;
  
  // Consent
  consent: boolean;
  termsAccepted: boolean;
}

interface FormErrors {
  [key: string]: string | undefined;
}

export default function JobApplication() {
  const [, setLocation] = useLocation();
  const { jobTitle } = useJobTitleFromUrl();
  const [formData, setFormData] = useState<FormData>({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    address: '',
    city: '',
    state: '',
    zipCode: '',
    country: 'United States',
    linkedin: '',
    twitter: '',
    github: '',
    website: '',
    currentPosition: '',
    currentCompany: '',
    yearsExperience: '',
    education: '',
    coverLetter: '',
    resume: null,
    race: '',
    veteranStatus: '',
    disabilityStatus: '',
    consent: false,
    termsAccepted: false,
  });

  const [errors, setErrors] = useState<FormErrors>({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleInputChange = (field: keyof FormData, value: string | boolean | File | null) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    // Clear error when user starts typing
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  const handleFileUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0] || null;
    handleInputChange('resume', file);
  };

  const validateForm = (): boolean => {
    const newErrors: FormErrors = {};

    // Required fields validation
    if (!formData.firstName.trim()) newErrors.firstName = 'First name is required';
    if (!formData.lastName.trim()) newErrors.lastName = 'Last name is required';
    if (!formData.email.trim()) newErrors.email = 'Email is required';
    if (!formData.phone.trim()) newErrors.phone = 'Phone number is required';
    if (!formData.address.trim()) newErrors.address = 'Address is required';
    if (!formData.city.trim()) newErrors.city = 'City is required';
    if (!formData.state.trim()) newErrors.state = 'State is required';
    if (!formData.zipCode.trim()) newErrors.zipCode = 'ZIP code is required';
    if (!formData.currentPosition.trim()) newErrors.currentPosition = 'Current position is required';
    if (!formData.yearsExperience.trim()) newErrors.yearsExperience = 'Years of experience is required';
    if (!formData.education.trim()) newErrors.education = 'Education is required';
    if (!formData.resume) newErrors.resume = 'Resume is required';
    if (!formData.consent) newErrors.consent = 'You must consent to data processing';
    if (!formData.termsAccepted) newErrors.termsAccepted = 'You must accept the terms and conditions';

    // Email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (formData.email && !emailRegex.test(formData.email)) {
      newErrors.email = 'Please enter a valid email address';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);
    
    try {
      // Get career_id from URL
      const currentPath = window.location.pathname;
      const slug = currentPath.split('/').pop();
      
      if (!slug) {
        throw new Error('Unable to determine job position');
      }

      // Get career data to find career_id
      const careerResponse = await fetch(`/api/careers/slug/${slug}`);
      if (!careerResponse.ok) {
        throw new Error('Unable to find job position');
      }
      const career = await careerResponse.json();

      // Prepare form data for API
      const formDataToSend = new FormData();
      formDataToSend.append('first_name', formData.firstName);
      formDataToSend.append('last_name', formData.lastName);
      formDataToSend.append('email', formData.email);
      formDataToSend.append('phone', formData.phone);
      formDataToSend.append('address', formData.address);
      formDataToSend.append('city', formData.city);
      formDataToSend.append('state', formData.state);
      formDataToSend.append('zip_code', formData.zipCode);
      formDataToSend.append('country', formData.country);
      formDataToSend.append('linkedin', formData.linkedin);
      formDataToSend.append('twitter', formData.twitter);
      formDataToSend.append('github', formData.github);
      formDataToSend.append('website', formData.website);
      formDataToSend.append('current_position', formData.currentPosition);
      formDataToSend.append('current_company', formData.currentCompany);
      formDataToSend.append('years_experience', formData.yearsExperience);
      formDataToSend.append('education', formData.education);
      formDataToSend.append('cover_letter', formData.coverLetter);
      formDataToSend.append('race', formData.race);
      formDataToSend.append('veteran_status', formData.veteranStatus);
      formDataToSend.append('disability_status', formData.disabilityStatus);
      formDataToSend.append('career_id', career.id.toString());
      
      if (formData.resume) {
        formDataToSend.append('resume', formData.resume);
      }

      // Submit application
      const response = await fetch('/api/candidates/apply', {
        method: 'POST',
        body: formDataToSend,
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to submit application');
      }

      const result = await response.json();
      
      // Redirect to thank you page with application ID
      setLocation(`/application/thank-you?applicationId=${result.application_id}&candidateName=${encodeURIComponent(`${formData.firstName} ${formData.lastName}`)}&jobTitle=${encodeURIComponent(career.title)}`);
      
    } catch (error) {
      console.error('Error submitting application:', error);
      alert(`There was an error submitting your application: ${error instanceof Error ? error.message : 'Please try again.'}`);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div>
      <main className="py-20 px-4 sm:px-6 lg:px-8 max-w-4xl mx-auto">
        {/* Page Header */}
        <div className="mb-8">
          <Button
            variant="ghost"
            onClick={() => setLocation('/careers')}
            className="mb-4 flex items-center text-muted-foreground hover:text-foreground"
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to Careers
          </Button>
          <h1 className="text-3xl font-bold text-foreground">Job Application</h1>
          <p className="text-lg font-semibold text-foreground mt-2">{jobTitle}</p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-8">
          {/* Personal Information */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <User className="h-5 w-5" />
                Personal Information
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <Label htmlFor="firstName">First Name *</Label>
                  <Input
                    id="firstName"
                    value={formData.firstName}
                    onChange={(e) => handleInputChange('firstName', e.target.value)}
                    className={errors.firstName ? 'border-red-500' : ''}
                  />
                  {errors.firstName && <p className="text-red-500 text-sm mt-1">{errors.firstName}</p>}
                </div>
                <div>
                  <Label htmlFor="lastName">Last Name *</Label>
                  <Input
                    id="lastName"
                    value={formData.lastName}
                    onChange={(e) => handleInputChange('lastName', e.target.value)}
                    className={errors.lastName ? 'border-red-500' : ''}
                  />
                  {errors.lastName && <p className="text-red-500 text-sm mt-1">{errors.lastName}</p>}
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <Label htmlFor="email">Email Address *</Label>
                  <Input
                    id="email"
                    type="email"
                    value={formData.email}
                    onChange={(e) => handleInputChange('email', e.target.value)}
                    className={errors.email ? 'border-red-500' : ''}
                  />
                  {errors.email && <p className="text-red-500 text-sm mt-1">{errors.email}</p>}
                </div>
                <div>
                  <Label htmlFor="phone">Phone Number *</Label>
                  <Input
                    id="phone"
                    type="tel"
                    value={formData.phone}
                    onChange={(e) => handleInputChange('phone', e.target.value)}
                    className={errors.phone ? 'border-red-500' : ''}
                  />
                  {errors.phone && <p className="text-red-500 text-sm mt-1">{errors.phone}</p>}
                </div>
              </div>

              <div>
                <Label htmlFor="address">Street Address *</Label>
                <Input
                  id="address"
                  value={formData.address}
                  onChange={(e) => handleInputChange('address', e.target.value)}
                  className={errors.address ? 'border-red-500' : ''}
                />
                {errors.address && <p className="text-red-500 text-sm mt-1">{errors.address}</p>}
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <div>
                  <Label htmlFor="city">City *</Label>
                  <Input
                    id="city"
                    value={formData.city}
                    onChange={(e) => handleInputChange('city', e.target.value)}
                    className={errors.city ? 'border-red-500' : ''}
                  />
                  {errors.city && <p className="text-red-500 text-sm mt-1">{errors.city}</p>}
                </div>
                <div>
                  <Label htmlFor="state">State/Province *</Label>
                  <Input
                    id="state"
                    value={formData.state}
                    onChange={(e) => handleInputChange('state', e.target.value)}
                    className={errors.state ? 'border-red-500' : ''}
                  />
                  {errors.state && <p className="text-red-500 text-sm mt-1">{errors.state}</p>}
                </div>
                <div>
                  <Label htmlFor="zipCode">ZIP/Postal Code *</Label>
                  <Input
                    id="zipCode"
                    value={formData.zipCode}
                    onChange={(e) => handleInputChange('zipCode', e.target.value)}
                    className={errors.zipCode ? 'border-red-500' : ''}
                  />
                  {errors.zipCode && <p className="text-red-500 text-sm mt-1">{errors.zipCode}</p>}
                </div>
              </div>

              <div>
                <Label htmlFor="country">Country</Label>
                <select
                  id="country"
                  value={formData.country}
                  onChange={(e) => handleInputChange("country", e.target.value)}
                  className="flex h-9 w-full rounded-md border border-border bg-background px-3 py-1 text-sm shadow-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
                >
                  <option value="United States">United States</option>
                  <option value="Canada">Canada</option>
                  <option value="United Kingdom">United Kingdom</option>
                  <option value="Germany">Germany</option>
                  <option value="France">France</option>
                  <option value="Other">Other</option>
                </select>
              </div>

              {/* Social Media Profiles */}
              <div className="space-y-4">
                <h4 className="text-lg font-medium text-gray-900">Social Media Profiles (Optional)</h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <Label htmlFor="linkedin">LinkedIn Profile</Label>
                    <Input
                      id="linkedin"
                      type="url"
                      placeholder="https://linkedin.com/in/yourprofile"
                      value={formData.linkedin}
                      onChange={(e) => handleInputChange('linkedin', e.target.value)}
                    />
                  </div>
                  <div>
                    <Label htmlFor="twitter">Twitter/X Profile</Label>
                    <Input
                      id="twitter"
                      type="url"
                      placeholder="https://twitter.com/yourhandle"
                      value={formData.twitter}
                      onChange={(e) => handleInputChange('twitter', e.target.value)}
                    />
                  </div>
                  <div>
                    <Label htmlFor="github">GitHub Profile</Label>
                    <Input
                      id="github"
                      type="url"
                      placeholder="https://github.com/yourusername"
                      value={formData.github}
                      onChange={(e) => handleInputChange('github', e.target.value)}
                    />
                  </div>
                  <div>
                    <Label htmlFor="website">Personal Website</Label>
                    <Input
                      id="website"
                      type="url"
                      placeholder="https://yourwebsite.com"
                      value={formData.website}
                      onChange={(e) => handleInputChange('website', e.target.value)}
                    />
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Professional Information */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Briefcase className="h-5 w-5" />
                Professional Information
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <Label htmlFor="currentPosition">Current Position *</Label>
                  <Input
                    id="currentPosition"
                    value={formData.currentPosition}
                    onChange={(e) => handleInputChange('currentPosition', e.target.value)}
                    className={errors.currentPosition ? 'border-red-500' : ''}
                  />
                  {errors.currentPosition && <p className="text-red-500 text-sm mt-1">{errors.currentPosition}</p>}
                </div>
                <div>
                  <Label htmlFor="currentCompany">Current Company</Label>
                  <Input
                    id="currentCompany"
                    value={formData.currentCompany}
                    onChange={(e) => handleInputChange('currentCompany', e.target.value)}
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <Label htmlFor="yearsExperience">Years of Experience *</Label>
                  <select
                    id="yearsExperience"
                    value={formData.yearsExperience}
                    onChange={(e) => handleInputChange("yearsExperience", e.target.value)}
                    className={`flex h-9 w-full rounded-md border border-border bg-background px-3 py-1 text-sm shadow-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary ${errors.yearsExperience ? "border-red-500" : ""}`}
                  >
                    <option value="" disabled>
                      Select experience level
                    </option>
                    <option value="0-1">0-1 years</option>
                    <option value="2-3">2-3 years</option>
                    <option value="4-5">4-5 years</option>
                    <option value="6-10">6-10 years</option>
                    <option value="10+">10+ years</option>
                  </select>
                  {errors.yearsExperience && <p className="text-red-500 text-sm mt-1">{errors.yearsExperience}</p>}
                </div>
                <div>
                  <Label htmlFor="education">Education *</Label>
                  <select
                    id="education"
                    value={formData.education}
                    onChange={(e) => handleInputChange("education", e.target.value)}
                    className={`flex h-9 w-full rounded-md border border-border bg-background px-3 py-1 text-sm shadow-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary ${errors.education ? "border-red-500" : ""}`}
                  >
                    <option value="" disabled>
                      Select education level
                    </option>
                    <option value="High School">High School</option>
                    <option value="Associate">Associate Degree</option>
                    <option value="Bachelor">Bachelor's Degree</option>
                    <option value="Master">Master's Degree</option>
                    <option value="PhD">PhD</option>
                    <option value="Other">Other</option>
                  </select>
                  {errors.education && <p className="text-red-500 text-sm mt-1">{errors.education}</p>}
                </div>
              </div>

              <div>
                <Label htmlFor="coverLetter">Cover Letter</Label>
                <Textarea
                  id="coverLetter"
                  value={formData.coverLetter}
                  onChange={(e) => handleInputChange('coverLetter', e.target.value)}
                  placeholder="Tell us why you're interested in this position and what makes you a great fit..."
                  rows={6}
                />
              </div>
            </CardContent>
          </Card>

          {/* Resume Upload */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <FileText className="h-5 w-5" />
                Resume Upload
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="border-2 border-dashed border-gray-300 rounded-lg p-8 text-center">
                <Upload className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                <div className="space-y-2">
                  <Label htmlFor="resume" className="cursor-pointer">
                    <span className="text-lg font-medium text-gray-900">Upload your resume</span>
                    <p className="text-sm text-gray-500">PDF, DOC, or DOCX files only (Max 10MB)</p>
                  </Label>
                  <Input
                    id="resume"
                    type="file"
                    accept=".pdf,.doc,.docx"
                    onChange={handleFileUpload}
                    className="hidden"
                  />
                </div>
                {formData.resume && (
                  <div className="mt-4 p-3 bg-green-50 border border-green-200 rounded-lg">
                    <p className="text-sm text-green-800">
                      <FileText className="h-4 w-4 inline mr-2" />
                      {String(formData.resume.name)}
                    </p>
                  </div>
                )}
                {errors.resume && <p className="text-red-500 text-sm mt-2">{errors.resume}</p>}
              </div>
            </CardContent>
          </Card>

          {/* Equal Opportunity Information */}
          <Card>
            <CardHeader>
              <CardTitle>Equal Opportunity Information</CardTitle>
              <p className="text-sm text-gray-600">
                This information is voluntary and will be kept confidential. It helps us ensure equal opportunity in our hiring process.
              </p>
            </CardHeader>
            <CardContent className="space-y-6">
              <div>
                <Label htmlFor="race">Race/Ethnicity</Label>
                <select
                  id="race"
                  value={formData.race}
                  onChange={(e) => handleInputChange("race", e.target.value)}
                  className="flex h-9 w-full rounded-md border border-border bg-background px-3 py-1 text-sm shadow-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
                >
                  <option value="" disabled>
                    Select race/ethnicity (optional)
                  </option>
                  <option value="prefer-not-to-say">Prefer not to say</option>
                  <option value="american-indian">American Indian or Alaska Native</option>
                  <option value="asian">Asian</option>
                  <option value="black">Black or African American</option>
                  <option value="hispanic">Hispanic or Latino</option>
                  <option value="native-hawaiian">Native Hawaiian or Other Pacific Islander</option>
                  <option value="white">White</option>
                  <option value="two-or-more">Two or More Races</option>
                  <option value="other">Other</option>
                </select>
              </div>

              <div>
                <Label htmlFor="veteranStatus">Veteran Status</Label>
                <select
                  id="veteranStatus"
                  value={formData.veteranStatus}
                  onChange={(e) => handleInputChange("veteranStatus", e.target.value)}
                  className="flex h-9 w-full rounded-md border border-border bg-background px-3 py-1 text-sm shadow-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
                >
                  <option value="" disabled>
                    Select veteran status (optional)
                  </option>
                  <option value="prefer-not-to-say">Prefer not to say</option>
                  <option value="not-veteran">I am not a veteran</option>
                  <option value="veteran">I am a veteran</option>
                  <option value="protected-veteran">I am a protected veteran</option>
                </select>
              </div>

              <div>
                <Label htmlFor="disabilityStatus">Disability Status</Label>
                <select
                  id="disabilityStatus"
                  value={formData.disabilityStatus}
                  onChange={(e) => handleInputChange("disabilityStatus", e.target.value)}
                  className="flex h-9 w-full rounded-md border border-border bg-background px-3 py-1 text-sm shadow-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
                >
                  <option value="" disabled>
                    Select disability status (optional)
                  </option>
                  <option value="prefer-not-to-say">Prefer not to say</option>
                  <option value="no-disability">I do not have a disability</option>
                  <option value="disability">I have a disability</option>
                  <option value="prefer-not-to-answer">Prefer not to answer</option>
                </select>
              </div>
            </CardContent>
          </Card>

          {/* Consent and Terms */}
          <Card>
            <CardHeader>
              <CardTitle>Consent and Terms</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-start space-x-3">
                <Checkbox
                  id="consent"
                  checked={formData.consent}
                  onChange={(e) => handleInputChange("consent", (e.target as HTMLInputElement).checked)}
                  className={errors.consent ? 'border-red-500' : ''}
                />
                <div className="space-y-1">
                  <Label htmlFor="consent" className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70">
                    Data Processing Consent *
                  </Label>
                  <p className="text-sm text-gray-600">
                    I consent to the processing of my personal data for the purpose of this job application and recruitment process. 
                    I understand that my data will be stored securely and used only for legitimate business purposes.
                  </p>
                  {errors.consent && <p className="text-red-500 text-sm">{errors.consent}</p>}
                </div>
              </div>

              <div className="flex items-start space-x-3">
                <Checkbox
                  id="termsAccepted"
                  checked={formData.termsAccepted}
                  onChange={(e) => handleInputChange("termsAccepted", (e.target as HTMLInputElement).checked)}
                  className={errors.termsAccepted ? 'border-red-500' : ''}
                />
                <div className="space-y-1">
                  <Label htmlFor="termsAccepted" className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70">
                    Terms and Conditions *
                  </Label>
                  <p className="text-sm text-gray-600">
                    I have read and agree to the{' '}
                    <a href="#" className="text-blue-600 hover:underline">Terms of Service</a>
                    {' '}and{' '}
                    <a href="#" className="text-blue-600 hover:underline">Privacy Policy</a>
                    . I understand that any false information provided may result in the rejection of my application.
                  </p>
                  {errors.termsAccepted && <p className="text-red-500 text-sm">{errors.termsAccepted}</p>}
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Submit Button */}
          <div className="flex justify-end space-x-4">
            <Button
              type="button"
              variant="outline"
              onClick={() => setLocation('/careers')}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={isSubmitting}
              className="min-w-[120px]"
            >
              {isSubmitting ? 'Submitting...' : 'Submit Application'}
            </Button>
          </div>
        </form>
      </main>
    </div>
  );
}
